import React, { useEffect, useState } from "react";
import mainLogo from "../../../images/smoodi_Logo.png";
import { Formik } from "formik";
import "../WorkOrders/WorkOrder.css";
import "./SiteVisitation.css";
import { SiteVisitationSteps, useSiteVisitation } from "../../providers/SiteVisitationProvider";
import { useParams } from "react-router-dom";
import MachineList from "./Tabs/MachineList";
import { updateJob, updateJobStatus } from "../../models/_publicCall";
import Moment from "moment";
import toast, { Toaster } from "react-hot-toast";
import { SiteVisitationPreview } from "./Tabs/SiteVisitationPreview";
import { useThemeMode } from "../../../_metronic/partials/layout/theme-mode/ThemeModeProvider";
import TicketsInfo from "./Tabs/TicketsInfo";
import CreateTicket from "./Tabs/CreateTicket";
import MachineInfo from "./Tabs/MachineInfo";
import ReplaceMachine from './Tabs/ReplaceMachine';
import store from "store2";
import { LocationInfo } from "./Tabs/LocationInfo";
import { LocationMedia } from "./Tabs/LocationMedia";

export const SiteVisitation: React.FC = () => {
  const {
    loading,
    machineServicesDB,
    machinesDB,
    blendersDB,
    tubeShieldsDB,
    locationsDB,
    boardsDB,
    logJob,
    diagnosis,
    flavors,
    issueRepotedDB,
    ticketTypes,
    logJobBasic,
    logJobMachineList,
    logJobMachineDetail,
    fetchSiteVisitJobOrder,
    fetchSiteVisitJobOrderBasic,
    fetchSiteVisitJobOrderMachineList,
    categoriesDB,
    cupHolderDB
  } = useSiteVisitation();

  const { id } = useParams();
  const { mode } = useThemeMode();

  // State
  const [workRunning, setWorkRunning] = useState(false);
  const [onWay, setOnWay] = useState(false);
  const [startWork, setStartWork] = useState(false);
  const [showJobCompleted, setShowJobCompleted] = useState(false);
  const [selectSiteVisitationMachine, setSelectSiteVisitationMachine] = useState<{ [machineId: string]: any }>({});
  const [showLoaderForSave, setShowLoaderForSave] = useState(false);
  const [travelStartTime, setTravelStartTime] = useState<string | null>(null);
  const [workStartTime, setWorkStartTime] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(SiteVisitationSteps.ON_MY_WAY);

  // Editable contact info
  const [isEditing, setIsEditing] = useState<{ [key: string]: boolean }>({});
  const [editableValues, setEditableValues] = useState<{
    contactName?: string;
    contactPhone?: string;
    contactEmail?: string;
  }>({
    contactName: logJobBasic?.location?.contactName || "",
    contactPhone: logJobBasic?.location?.contactPhone || "",
    contactEmail: logJobBasic?.location?.contactEmail || "",
  });

  // WiFi credentials
  let wifiName = "";
  let wifiPassword = "";
  const rawWifiCredentials = logJobBasic?.location?.wifiCredentials;
  if (typeof rawWifiCredentials === "string") {
    try {
      const parsed = JSON.parse(rawWifiCredentials);
      wifiName = parsed?.wifiName ?? "";
      wifiPassword = parsed?.wifiPassword ?? "";
    } catch {
      // ignore parse error
    }
  }

  // Effects
  useEffect(() => {
    if (!logJob || !logJob.job) return;
    const jobResult = logJob.job;
    if (jobResult.travelingStartTime) {
      setTravelStartTime(Moment.utc(jobResult.travelingStartTime).local().format("HH:mm:ss"));
      if (jobResult.travelingEndTime === null) setOnWay(true);
    }
    if (jobResult.workStartTime) {
      setWorkStartTime(Moment.utc(jobResult.workStartTime).local().format("HH:mm:ss"));
      if (!jobResult.closingDateAndTime) {
        setOnWay(true);
        setWorkRunning(true);
        setStartWork(true);
        setCurrentStep(SiteVisitationSteps.MACHINE_LIST);
      }
    }
    if ((jobResult.status as string) === "Completed") {
      setShowJobCompleted(true);
    }
  }, [logJob]);

  useEffect(() => {
    if (!logJobBasic || !logJobBasic.job) return;
    const jobResult = logJobBasic.job;
    if (jobResult.travelingStartTime) {
      setTravelStartTime(Moment.utc(jobResult.travelingStartTime).local().format("HH:mm:ss"));
      if (jobResult.travelingEndTime === null) setOnWay(true);
    }
    if (jobResult.workStartTime) {
      setWorkStartTime(Moment.utc(jobResult.workStartTime).local().format("HH:mm:ss"));
      if (!jobResult.closingDateAndTime) {
        setOnWay(true);
        fetchSiteVisitJobOrderMachineList(jobResult.id);
        setWorkRunning(true);
        setStartWork(true);
        setCurrentStep(SiteVisitationSteps.MACHINE_LIST);
      }
    }
    if ((jobResult.status as string) === "Completed") {
      fetchSiteVisitJobOrder(jobResult.id);
      setShowJobCompleted(true);
    }
  }, [logJobBasic]);

  useEffect(() => {
    if (travelStartTime) setOnWay(true);
    if (workStartTime) {
      setStartWork(true);
      setWorkRunning(true);
    }
  }, [travelStartTime, workStartTime]);

  useEffect(() => {
    if (!id) return;
    fetchSiteVisitJobOrderBasic(id);
  }, [id]);

  // Handlers
  const handleUpdateStatus = async () => {
    if (!logJobBasic) return;
    const date = Moment.utc().format();
    const result = await updateJobStatus(logJobBasic.job.id, "ON_MY_WAY", date);
    if (result?.isSuccess) {
      setOnWay(true);
      setTravelStartTime(Moment().local().format("HH:mm:ss"));
      setCurrentStep(SiteVisitationSteps.START_WORK);
    } else {
      console.error("Failed to update status:", result?.message);
    }
  };

  const handleStartWork = async () => {
    if (!logJobBasic) return;
    const date = Moment.utc().format();
    const result = await updateJobStatus(logJobBasic.job.id, "START_WORK", date);
    if (result?.isSuccess) {
      fetchSiteVisitJobOrderMachineList(logJobBasic.job.id);
      setStartWork(true);
      setWorkRunning(true);
      setWorkStartTime(Moment().local().format("HH:mm:ss"));
      setCurrentStep(SiteVisitationSteps.MACHINE_LIST);
    } else {
      console.error("Failed to start work:", result?.message);
    }
  };

  const handleEditClick = (field: string) => setIsEditing((prev) => ({ ...prev, [field]: true }));
  const handleInputChange = (field: string, value: string) => setEditableValues((prev) => ({ ...prev, [field]: value }));
  const handleInputBlur = (field: string) => setIsEditing((prev) => ({ ...prev, [field]: false }));

  // Render
  return (
    <>
      <div className={`wo-body ${mode === "dark" ? "dark-wo-body" : ""}`}>
        <div className="wo-logo-container">
          <img src={mainLogo} alt="Smoody Logo" className="wo-logo" />
        </div>
        <div className="wo-form-wrapper">
          <div className={`wo-header-container ${mode === "dark" ? "dark-wo-header-container" : ""}`}>
            <h2 className="wo-header-title">Site Visitation</h2>
          </div>
        </div>
        {!loading && logJobBasic && logJobBasic.job && !showJobCompleted && (
          <div className="wo-form-wrapper">
            <div className={`wo-form-container ${mode === "dark" ? "dark-theme" : ""}`}>
              <div className="row">
                <Formik
                  initialValues={{
                    locationId: logJobBasic?.location?.id,
                    address: logJobBasic?.location?.address,
                    tickets: [],
                    blenderId: "",
                    tubeShieldId: "",
                    cupHolderId: "",
                    flowmeterActivated: "",
                    hasPolyfuses: "",
                    made4Smoothies: "",
                    cleanlinessCheck: "",
                    getStatusPassed: "",
                    waterLevelCalibratedWithScale: "",
                    operations: [],
                    marketingMaterials: [],
                    oldMachineShiftToLocationId: "",
                    newMachineId: "",
                    convertToLabTicket: false,
                    flavorPrices:
                      flavors?.map((flavor: any) => {
                        const existingPrice = logJobBasic.flavorPrices?.find(
                          (flavorPrice: any) =>
                            flavorPrice.flavor === flavor.flavor
                        );
                        return {
                          flavor: flavor.flavor,
                          price: existingPrice ? existingPrice.price : 0,
                          is_active: existingPrice ? existingPrice.isActive : false,
                        };
                      }) || [],
                    smoothieSetupMedia: [],
                    marketingMaterialPresent: [],
                    hasWifiExtender: logJobBasic.location.hasWifiExtender ?? false,
                    hasSmoodiRouter: logJobBasic.location.hasSmoodiRouter ?? false,
                    hasPressurePump: logJobBasic.location.hasPressurePump ?? false,
                    hasPressureTank: logJobBasic.location.hasPressureTank ?? false,
                    actionNoteCategoryId: (() => {
                      const jobId = logJobBasic?.job?.id;
                      const jobData = store.get("job_id_data") || {};
                      return jobId && jobData[jobId]?.actionNoteCategoryId ? jobData[jobId].actionNoteCategoryId : "";
                    })(),
                    additionalNoteCategoryId: (() => {
                      const jobId = logJobBasic?.job?.id;
                      const jobData = store.get("job_id_data") || {};
                      return jobId && jobData[jobId]?.additionalNoteCategoryId ? jobData[jobId].additionalNoteCategoryId : "";
                    })(),
                    actionItems: (() => {
                      const jobId = logJobBasic?.job?.id;
                      const jobData = store.get("job_id_data") || {};
                      return jobId && jobData[jobId]?.actionItems ? jobData[jobId].actionItems : "";
                    })(),
                    additionalComments: (() => {
                      const jobId = logJobBasic?.job?.id;
                      const jobData = store.get("job_id_data") || {};
                      return jobId && jobData[jobId]?.additionalComments ? jobData[jobId].additionalComments : "";
                    })(),
                    contactName: logJobBasic?.location?.contactName || "",
                    contactEmail: logJobBasic?.location?.contactEmail || "",
                    contactPhone: logJobBasic?.location?.contactPhone || "",
                  }}
                  onSubmit={async (values) => {
                    if (!logJobBasic) return;
                    const jobData = store.get("job_id_data") || {};
                    const jobId = logJobBasic.job.id;
                    const storeData = jobData[jobId] || {};
                    const machines = storeData.machines || {};
                    const tickets = storeData.tickets || [];
                    const storedLocation = storeData.location || {};
                    const storedImages = storeData.images || {};
                    const operations = storedLocation.selectedOperations || [];
                    const operationOther = storedLocation.operationOther || "";
                    const marketingMaterials = storedLocation.selectedMarketingMaterials || [];
                    const flavorPrices = storeData.flavorPrices || values.flavorPrices;
                    const smoothieSetupMedia = storedImages.smoothieSetupMedia || [];
                    const marketingMaterialPresent = storedImages.marketingMaterialPresent || [];
                    const hasSmoodiRouter = storedLocation.hasSmoodiRouter ?? values.hasSmoodiRouter;
                    const hasWifiExtender = storedLocation.hasWifiExtender ?? values.hasWifiExtender;
                    const hasPressurePump = storedLocation.hasPressurePump ?? values.hasPressurePump;
                    const hasPressureTank = storedLocation.hasPressureTank ?? values.hasPressureTank;
                    const actionNoteCategoryId= storeData.actionNoteCategoryId  !== undefined ? storeData.actionNoteCategoryId:  values.actionNoteCategoryId;
                    const  additionalNoteCategoryId= storeData.additionalNoteCategoryId  !== undefined ? storeData.additionalNoteCategoryId:values.additionalNoteCategoryId;
                    const actionItems = storeData.actionItems !== undefined ? storeData.actionItems : values.actionItems;
                    const additionalComments = storeData.additionalComments !== undefined ? storeData.additionalComments : values.additionalComments;
                    const contactName =
                      editableValues.contactName ||
                      storeData.contactName ||
                      logJobBasic?.location?.contactName ||
                      "";
                    const contactEmail =
                      editableValues.contactEmail ||
                      storeData.contactEmail ||
                      logJobBasic?.location?.contactEmail ||
                      "";
                    const contactPhone =
                      editableValues.contactPhone ||
                      storeData.contactPhone ||
                      logJobBasic?.location?.contactPhone ||
                      "";
                    const updatedTickets = tickets.map((ticket: any) => {
                      if(ticket.type !== 'new') return ticket

                      return {
                        ...ticket,
                        issuesReported: ticket.issuesReported.map((issue: any) => issue.id),
                        machineServices: ticket.machineServices.map((service: any) => service.id),
                      }
                    })
                    const $inputData: any = {
                      locationId: logJobBasic?.location?.id,
                      address: logJobBasic?.location?.address,
                      contactName,
                      contactEmail,
                      contactPhone,
                      actionNoteCategoryId,
                      additionalNoteCategoryId,
                      actionItems,
                      additionalComments,
                      tickets: updatedTickets,
                      operations,
                      operationOther,
                      machines: Object.values(machines),
                      marketingMaterials,
                      flavorPrices,
                      smoothieSetupMedia,
                      marketingMaterialPresent,
                      hasSmoodiRouter,
                      hasWifiExtender,
                      hasPressurePump,
                      hasPressureTank
                    };

                    setShowLoaderForSave(true);
                    const response = await updateJob(jobId, $inputData);
                    if(response?.isSuccess) {
                      toast.success("Job updated successfully");
                      const updatedJobData = { ...jobData };
                      delete updatedJobData[jobId];
                      store.set("job_id_data", updatedJobData);
                      setShowLoaderForSave(false);
                      fetchSiteVisitJobOrder(jobId);
                    } else {
                      toast.error("Failed to update job");
                      setShowLoaderForSave(false);
                      console.error("Failed to update job:", response?.message);
                    }
                  }}
                >
                  {({
                    handleChange,
                    handleSubmit,
                    values,
                    setFieldValue,
                  }) => (
                    <form onSubmit={handleSubmit} autoComplete="off">
                      <div>
                        {currentStep === SiteVisitationSteps.ON_MY_WAY && (
                          <>
                            <header className="header">
                              <h2>Location Information</h2>
                            </header>
                            <div className="machine-wrapper">
                              <div className="col-6">
                                <div className="d-flex align-items-center gap-2">
                                  <label htmlFor="locationId" className="wo-label fs-5 mb-0 fw-bold">
                                    Location Name:
                                  </label>
                                  <span className="mb-0 fs-5 text-muted">
                                    {logJobBasic?.location?.name}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <label htmlFor="address" className="wo-label fs-5 mb-0 fw-bold">
                                    Address:
                                  </label>
                                  <span className="mb-0 fs-5 text-muted">
                                    {logJobBasic?.location?.address}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <label htmlFor="contactName" className="wo-label fs-5 mb-0 fw-bold">
                                    Contact Name:
                                  </label>
                                  {isEditing.contactName ? (
                                    <input
                                      type="text"
                                      className="form-control"
                                      defaultValue={logJobBasic?.location?.contactName}
                                      onChange={(e) => handleInputChange("contactName", e.target.value)}
                                      onBlur={() => handleInputBlur("contactName")}
                                    />
                                  ) : (
                                    <>
                                      <span className="mb-0 fs-5 text-muted">
                                        {editableValues.contactName
                                          ? editableValues.contactName
                                          : logJobBasic?.location?.contactName || "-"}
                                      </span>
                                      <i
                                        className="bi bi-pencil-square cursor-pointer text-hover-primary fs-2"
                                        onClick={() => handleEditClick("contactName")}
                                      ></i>
                                    </>
                                  )}
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <label htmlFor="contactPhone" className="wo-label fs-5 mb-0 fw-bold">
                                    Contact Phone:
                                  </label>
                                  {isEditing.contactPhone ? (
                                    <input
                                      type="text"
                                      className="form-control"
                                      defaultValue={logJobBasic?.location?.contactPhone}
                                      onChange={(e) => handleInputChange("contactPhone", e.target.value)}
                                      onBlur={() => handleInputBlur("contactPhone")}
                                    />
                                  ) : (
                                    <>
                                      <span className="mb-0 fs-5 text-muted">
                                        {editableValues.contactPhone
                                          ? editableValues.contactPhone
                                          : logJobBasic?.location?.contactPhone || "-"}
                                      </span>
                                      <i
                                        className="bi bi-pencil-square cursor-pointer text-hover-primary fs-2"
                                        onClick={() => handleEditClick("contactPhone")}
                                      ></i>
                                    </>
                                  )}
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <label htmlFor="contactEmail" className="wo-label fs-5 mb-0 fw-bold">
                                    Contact Email:
                                  </label>
                                  {isEditing.contactEmail ? (
                                    <input
                                      type="text"
                                      className="form-control"
                                      defaultValue={logJobBasic?.location?.contactEmail}
                                      onChange={(e) => handleInputChange("contactEmail", e.target.value)}
                                      onBlur={() => handleInputBlur("contactEmail")}
                                    />
                                  ) : (
                                    <>
                                      <span className="mb-0 fs-5 text-muted">
                                        {editableValues.contactEmail
                                          ? editableValues.contactEmail
                                          : logJobBasic?.location?.contactEmail || "-"}
                                      </span>
                                      <i
                                        className="bi bi-pencil-square cursor-pointer text-hover-primary fs-2"
                                        onClick={() => handleEditClick("contactEmail")}
                                      ></i>
                                    </>
                                  )}
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <label htmlFor="address" className="wo-label fs-5 mb-0 fw-bold">
                                    Distributor Name:
                                  </label>
                                  <span className="mb-0 fs-5 text-muted">
                                    {logJobBasic?.location?.distributorName || "-"}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <label htmlFor="wifiName" className="wo-label fs-5 mb-0 fw-bold">
                                    WiFi Name:
                                  </label>
                                  <span className="mb-0 fs-5 text-muted">
                                    {wifiName || "-"}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <label htmlFor="wifiPassword" className="wo-label fs-5 mb-0 fw-bold">
                                    WiFi Password:
                                  </label>
                                  <span className="mb-0 fs-5 text-muted">
                                    {wifiPassword || "-"}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-8 d-flex justify-content-center">
                              {travelStartTime && !startWork ? (
                                  <button
                                    type="button"
                                    onClick={handleUpdateStatus}
                                    className="btn btn-primary me-2"
                                  >
                                    On My Way
                                  </button>
                                ) : travelStartTime ? (
                                  <div className="btn btn-primary me-3 text-center">
                                    <div>Traveling At</div>
                                    <div>{travelStartTime || "Updating..."}</div>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={handleUpdateStatus}
                                    className="btn btn-primary me-2"
                                  >
                                    On My Way
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                        {currentStep === SiteVisitationSteps.START_WORK && onWay && (
                          <>
                            <header className="header">
                              <h2>Initiate Work </h2>
                            </header>
                            <div className="Initiate-wrapper">
                              <div className="col-12 d-flex justify-content-center">
                                {startWork ? (
                                  <div className="btn btn-primary">
                                    <div>Work At</div>
                                    <div>{workStartTime || "Updating..."}</div>
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-primary"
                                    onClick={handleStartWork}
                                    type="button"
                                  >
                                    Start Work
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                        {currentStep === SiteVisitationSteps.MACHINE_LIST && (
                          <MachineList
                            logJobId={logJobBasic.job.id}
                            logJobMachineList={logJobMachineList}
                            setSelectSiteVisitationMachine={setSelectSiteVisitationMachine}
                            logJobMachineDetail={logJobMachineDetail}
                            setCurrentStep={setCurrentStep}
                          />
                        )}
                        {currentStep === SiteVisitationSteps.TICKET_INFO && (
                          <TicketsInfo
                            jobId={logJobBasic.job.id}
                            selectedMachine={selectSiteVisitationMachine}
                            diagnosis={diagnosis}
                            blendersDB={blendersDB}
                            boardsDB={boardsDB}
                            machineServicesDB={machineServicesDB}
                            setCurrentStep={setCurrentStep}
                          />
                        )}
                        {currentStep === SiteVisitationSteps.CREATE_TICKET && (
                          <CreateTicket
                            jobId={logJobBasic.job.id}
                            setCurrentStep={setCurrentStep}
                            issueRepotedDB={issueRepotedDB}
                            ticketTypes={ticketTypes}
                            machineServicesDB={machineServicesDB}
                            diagnosisDB={diagnosis}
                            SiteVisitationSteps={SiteVisitationSteps}
                            selectSiteVisitationMachine={selectSiteVisitationMachine}
                          />
                        )}
                        {currentStep === SiteVisitationSteps.MACHINE_INFO && (
                          <MachineInfo
                            jobId={logJobBasic.job.id}
                            setFieldValue={setFieldValue}
                            selectSiteVisitationMachine={selectSiteVisitationMachine}
                            setCurrentStep={setCurrentStep}
                          />
                        )}
                        {currentStep === SiteVisitationSteps.REPLACE_MACHINE && (
                          <ReplaceMachine
                            jobId={logJobBasic.job.id}
                            handleChange={handleChange}
                            values={values}
                            setFieldValue={setFieldValue}
                            machinesDB={machinesDB}
                            locationsDB={locationsDB}
                            machineServicesDB={machineServicesDB}
                            setCurrentStep={setCurrentStep}
                            selectedMachine={selectSiteVisitationMachine}
                          />
                        )}
                        {currentStep === SiteVisitationSteps.LOCATION_INFO_1 && (
                          <LocationInfo
                            locationName={logJobBasic?.location?.name}
                            values={values}
                            setFieldValue={setFieldValue}
                            jobId={logJobBasic.job.id}
                            setCurrentStep={setCurrentStep}
                          />
                        )}
                        {currentStep === SiteVisitationSteps.LOCATION_INFO_2 && (
                          <LocationMedia
                            locationName={logJobBasic?.location?.name}
                            values={values}
                            setFieldValue={setFieldValue}
                            jobId={logJobBasic.job.id}
                            setCurrentStep={setCurrentStep}
                            handleChange={handleChange}
                            workRunning={workRunning}
                            showLoaderForSave={showLoaderForSave}
                            categoriesDB={categoriesDB}
                          />
                        )}
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        )}
        {loading && (
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {showJobCompleted && logJob && logJob.job && (
          <SiteVisitationPreview
            logJob={logJob}
            travelTimer={() => travelStartTime || ""}
            workTimer={() => workStartTime || ""}
            blendersDB={blendersDB}
            tubeShieldsDB={tubeShieldsDB}
            flavors={flavors}
            categoriesDB={categoriesDB}
            cupHolderDB={cupHolderDB}
          />
        )}
      </div>
      <Toaster
        position="bottom-left"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          className: "",
          duration: 3000,
        }}
      />
    </>
  );
};